import { useMemo, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import {
  type AdminPlugin,
  type AuthKind,
  type PluginAuth,
  type PluginOperation,
  pluginsApi,
  type PluginTemplate,
  type PluginVersion,
} from "@/api/plugins-api"
import { Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

import { PluginHeadersField } from "./plugin-headers-field"
import { PluginLimitsEditor } from "./plugin-limits-editor"
import {
  DEFAULT_PLUGIN_LIMITS,
  formatPluginHeaders,
  nativeVersionActions,
  parsePluginHeaders,
  pluginErrorCode,
  pluginErrorMessage,
  splitIdentifiers,
} from "./plugin-utils"
import { usePluginQueryKey } from "./use-plugin-query-key"

export function PluginDefinitionForm({
  plugin,
  latest,
  defaults,
  original,
  onSaved,
}: {
  plugin: AdminPlugin
  latest?: PluginVersion
  defaults?: PluginTemplate["defaults"]
  original: string
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const client = useQueryClient()
  const [baseUrl, setBaseUrl] = useState(latest?.base_url ?? defaults?.base_url ?? "")
  const [source, setSource] = useState(original)
  const initialAuth = latest?.auth ?? defaults?.auth
  const [authKind, setAuthKind] = useState<AuthKind>(initialAuth?.kind ?? "none")
  const [headerName, setHeaderName] = useState(latest?.auth.header_name ?? "X-API-Key")
  const [issuer, setIssuer] = useState(initialAuth?.issuer ?? "")
  const [authorizeUrl, setAuthorizeUrl] = useState(initialAuth?.authorization_endpoint ?? "")
  const [tokenUrl, setTokenUrl] = useState(initialAuth?.token_endpoint ?? "")
  const [revocationUrl, setRevocationUrl] = useState(initialAuth?.revocation_endpoint ?? "")
  const [clientId, setClientId] = useState(initialAuth?.client_id ?? "")
  const [clientSecret, setClientSecret] = useState("")
  const [resource, setResource] = useState(latest?.auth.resource ?? "")
  const [serverCredential, setServerCredential] = useState("")
  const [serverConnectionId, setServerConnectionId] = useState<string>()
  const [toolManifest, setToolManifest] = useState(
    defaults?.tool_manifest ? JSON.stringify(defaults.tool_manifest, null, 2) : ""
  )
  const [clientSecretReference, setClientSecretReference] = useState(
    latest?.auth.client_secret_connection_id
  )
  const [scopes, setScopes] = useState(initialAuth?.scopes?.join(" ") ?? "")
  const [privateCidrs, setPrivateCidrs] = useState(latest?.network?.private_cidrs.join(", ") ?? "")
  const [nativeTool, setNativeTool] = useState(
    latest?.operations[0]?.id ?? defaults?.tool_name ?? "web_search"
  )
  const [nativeActions, setNativeActions] = useState(
    nativeVersionActions(latest) || defaults?.approved_actions?.join(", ") || ""
  )
  const [limits, setLimits] = useState(latest?.limits ?? DEFAULT_PLUGIN_LIMITS)
  const [operations, setOperations] = useState<PluginOperation[]>([])
  const [approved, setApproved] = useState<string[]>([])
  const [readOnly, setReadOnly] = useState(false)
  const [fileError, setFileError] = useState(false)
  const [extraHeaders, setExtraHeaders] = useState(formatPluginHeaders(latest?.headers))
  const [transport, setTransport] = useState(latest?.transport ?? "streamable_http")
  const parsedHeaders = useMemo(
    () => parsePluginHeaders(extraHeaders, authKind === "api_key" ? headerName : undefined),
    [extraHeaders, authKind, headerName]
  )

  function values(): Record<string, unknown> {
    const auth: PluginAuth = { kind: authKind }
    if (resource) auth.resource = resource
    if (revocationUrl) auth.revocation_endpoint = revocationUrl
    if (clientSecretReference) auth.client_secret_connection_id = clientSecretReference
    if (authKind === "api_key") auth.header_name = headerName
    if (authKind === "oauth" || authKind === "entra")
      Object.assign(auth, {
        issuer,
        authorization_endpoint: authorizeUrl,
        token_endpoint: tokenUrl,
        client_id: clientId,
        scopes: splitIdentifiers(scopes),
      })
    if (plugin.plugin_kind === "native")
      return {
        tool_name: nativeTool,
        approved_actions: splitIdentifiers(nativeActions),
        auth,
        limits,
      }
    return {
      base_url: baseUrl,
      limits,
      auth,
      network: { private_cidrs: splitIdentifiers(privateCidrs) },
      headers: parsedHeaders.headers,
      ...(plugin.plugin_kind === "openapi" ? { source } : { transport }),
      ...(plugin.plugin_kind === "mcp" && serverConnectionId
        ? { connection_id: serverConnectionId }
        : {}),
      ...(plugin.plugin_kind === "mcp" && toolManifest.trim()
        ? { tool_manifest: JSON.parse(toolManifest) }
        : {}),
    }
  }
  const preview = useMutation({
    mutationFn: async () => {
      const data = values()
      if (
        plugin.plugin_kind === "mcp" &&
        serverCredential &&
        (authKind === "api_key" || authKind === "bearer")
      ) {
        const credential = serverCredential
        setServerCredential("")
        const connected = await pluginsApi.connectCompany(plugin.id, authKind, credential)
        setServerConnectionId(connected.connection.id)
        data.connection_id = connected.connection.id
      }
      return pluginsApi.preview(plugin, data)
    },
    onSuccess: (data) => {
      setOperations(data.operations)
      setApproved([])
    },
  })
  const publish = useMutation({
    mutationFn: async () => {
      const data = values()
      if (clientSecret) {
        const secret = clientSecret
        setClientSecret("")
        const result = await pluginsApi.oauthClient(plugin.id, secret)
        setClientSecretReference(result.client_secret_connection_id)
        data.auth = {
          ...(data.auth as PluginAuth),
          client_secret_connection_id: result.client_secret_connection_id,
        }
      }
      if (plugin.plugin_kind === "openapi") data.approved_ids = approved
      if (plugin.plugin_kind === "mcp")
        Object.assign(data, { approved_names: approved, confirmed_read_only: readOnly })
      return pluginsApi.publish(plugin, data)
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: pluginKey() })
      onSaved()
    },
  })
  const error = preview.error ?? publish.error
  const oauth = authKind === "oauth" || authKind === "entra"
  const headersInvalid = plugin.plugin_kind !== "native" && parsedHeaders.invalid
  const previewDisabled =
    preview.isPending || !baseUrl || headersInvalid || (plugin.plugin_kind === "openapi" && !source)
  return (
    <div className="space-y-4" data-testid="plugin-definition-editor">
      <p className="text-muted-foreground text-sm">{t("plugins.publishDescription")}</p>
      <PluginLimitsEditor value={limits} onChange={setLimits} />
      {plugin.plugin_kind !== "native" ? (
        <div className="space-y-2">
          <Label htmlFor="plugin-base-url">{t("plugins.baseUrl")}</Label>
          <Input
            id="plugin-base-url"
            placeholder="https://api.example.com/v1"
            type="url"
            value={baseUrl}
            onChange={(event) => {
              setBaseUrl(event.target.value)
              setOperations([])
              setApproved([])
            }}
          />
        </div>
      ) : (
        <>
          <Label>{t("plugins.tool")}</Label>
          <Select value={nativeTool} onValueChange={setNativeTool}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["web_search", "web_scrape", "library_search", "microsoft_365_search"].map(
                (name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
          {nativeTool === "microsoft_365_search" && (
            <div className="space-y-2">
              <Label htmlFor="plugin-native-actions">{t("plugins.readActions")}</Label>
              <Input
                id="plugin-native-actions"
                placeholder="get_emails, get_calendar_events"
                value={nativeActions}
                onChange={(event) => setNativeActions(event.target.value)}
              />
            </div>
          )}
        </>
      )}
      <div className="space-y-2">
        <Label>{t("plugins.authentication")}</Label>
        <Select value={authKind} onValueChange={(value) => setAuthKind(value as AuthKind)}>
          <SelectTrigger aria-label={t("plugins.authentication")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(["none", "api_key", "bearer", "entra", "oauth"] as AuthKind[]).map((kind) => (
              <SelectItem key={kind} value={kind}>
                {t(`plugins.auth.${kind}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {authKind === "api_key" && (
        <div className="space-y-2">
          <Label htmlFor="plugin-header">{t("plugins.headerName")}</Label>
          <Input
            id="plugin-header"
            value={headerName}
            onChange={(event) => setHeaderName(event.target.value)}
          />
        </div>
      )}
      {oauth && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="plugin-issuer">{t("plugins.issuer")}</Label>
            <Input
              id="plugin-issuer"
              value={issuer}
              onChange={(event) => setIssuer(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-client-id">{t("plugins.clientId")}</Label>
            <Input
              id="plugin-client-id"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-authorize">{t("plugins.authorizeUrl")}</Label>
            <Input
              id="plugin-authorize"
              value={authorizeUrl}
              onChange={(event) => setAuthorizeUrl(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-token-url">{t("plugins.tokenUrl")}</Label>
            <Input
              id="plugin-token-url"
              value={tokenUrl}
              onChange={(event) => setTokenUrl(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-revocation-url">{t("plugins.revocationUrl")}</Label>
            <Input
              id="plugin-revocation-url"
              value={revocationUrl}
              onChange={(event) => setRevocationUrl(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-client-secret">{t("plugins.clientSecret")}</Label>
            <Input
              autoComplete="off"
              id="plugin-client-secret"
              type="password"
              value={clientSecret}
              onChange={(event) => setClientSecret(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-resource">{t("plugins.oauthResource")}</Label>
            <Input
              id="plugin-resource"
              value={resource}
              onChange={(event) => setResource(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plugin-scopes">{t("plugins.permissions")}</Label>
            <Input
              id="plugin-scopes"
              value={scopes}
              onChange={(event) => setScopes(event.target.value)}
            />
          </div>
        </div>
      )}
      {plugin.plugin_kind !== "native" && (
        <div className="space-y-2">
          <Label htmlFor="plugin-private-networks">{t("plugins.privateNetworks")}</Label>
          <Input
            id="plugin-private-networks"
            placeholder="10.20.0.0/24"
            value={privateCidrs}
            onChange={(event) => setPrivateCidrs(event.target.value)}
          />
        </div>
      )}
      {plugin.plugin_kind === "openapi" && (
        <div className="space-y-2">
          <Label htmlFor="plugin-source">{t("plugins.openapi")}</Label>
          <Input
            accept=".json,.yaml,.yml"
            aria-label={t("plugins.upload")}
            type="file"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (!file) return
              if (file.size > 2 * 1024 * 1024) {
                setFileError(true)
                return
              }
              setFileError(false)
              void file.text().then((text) => {
                setSource(text)
                setOperations([])
                setApproved([])
              })
            }}
          />
          <Textarea
            className="font-mono text-xs"
            id="plugin-source"
            rows={10}
            value={source}
            onChange={(event) => {
              setSource(event.target.value)
              setOperations([])
              setApproved([])
            }}
          />
        </div>
      )}
      {plugin.plugin_kind !== "native" && (
        <PluginHeadersField
          value={extraHeaders}
          onChange={(next) => {
            setExtraHeaders(next)
            setOperations([])
            setApproved([])
          }}
        />
      )}
      {plugin.plugin_kind === "mcp" && (
        <div className="space-y-2">
          <Label>{t("plugins.transport")}</Label>
          <Select
            value={transport}
            onValueChange={(value) => setTransport(value === "sse" ? "sse" : "streamable_http")}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="streamable_http">Streamable HTTP</SelectItem>
              <SelectItem value="sse">SSE</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {plugin.plugin_kind === "mcp" && (
        <>
          {(authKind === "api_key" || authKind === "bearer") && (
            <div className="space-y-2">
              <Label htmlFor="mcp-import-credential">{t("plugins.importCredential")}</Label>
              <Input
                autoComplete="off"
                id="mcp-import-credential"
                type="password"
                value={serverCredential}
                onChange={(event) => setServerCredential(event.target.value)}
              />
            </div>
          )}
          <details>
            <summary className="cursor-pointer text-sm">{t("plugins.manifestImport")}</summary>
            <p className="text-muted-foreground my-2 text-xs">{t("plugins.manifestDescription")}</p>
            <Textarea
              aria-label={t("plugins.manifestImport")}
              className="font-mono text-xs"
              rows={6}
              value={toolManifest}
              onChange={(event) => {
                setToolManifest(event.target.value)
                setOperations([])
                setApproved([])
              }}
            />
          </details>
        </>
      )}
      {plugin.plugin_kind !== "native" && (
        <Button disabled={previewDisabled} variant="outline" onClick={() => preview.mutate()}>
          {preview.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
          {t("plugins.preview")}
        </Button>
      )}
      {operations.length > 0 && (
        <fieldset className="space-y-2 rounded-lg border p-3">
          <legend className="px-1 text-sm font-medium">{t("plugins.approveOperations")}</legend>
          <div className="max-h-64 space-y-2 overflow-auto">
            {operations.map((operation) => (
              <label
                key={operation.id}
                className="flex items-start gap-2 text-sm"
                htmlFor={`operation-${operation.id}`}
              >
                <Checkbox
                  checked={approved.includes(operation.id)}
                  id={`operation-${operation.id}`}
                  onCheckedChange={(checked) =>
                    setApproved((current) =>
                      checked
                        ? [...current, operation.id]
                        : current.filter((id) => id !== operation.id)
                    )
                  }
                />
                <span>
                  <span className="font-medium">{operation.id}</span>
                  <span className="text-muted-foreground block">{operation.description}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
      {plugin.plugin_kind === "mcp" && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={readOnly} onCheckedChange={(value) => setReadOnly(value === true)} />
          {t("plugins.confirmReadOnly")}
        </label>
      )}
      {(error || fileError || headersInvalid) && (
        <p className="text-destructive text-sm" role="alert">
          {fileError && t("plugins.fileTooLarge")}
          {!fileError && headersInvalid && pluginErrorMessage(t, "invalid_plugin_header")}
          {!fileError && !headersInvalid && pluginErrorMessage(t, pluginErrorCode(error))}
        </p>
      )}
      <Button
        disabled={
          publish.isPending ||
          headersInvalid ||
          (plugin.plugin_kind !== "native" && !approved.length) ||
          (plugin.plugin_kind === "mcp" && !readOnly)
        }
        onClick={() => publish.mutate()}
      >
        {publish.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
        {t("plugins.publish")}
      </Button>
    </div>
  )
}
